<script>
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import UserForm from '@/components/users/UserForm';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'EditProfile',
  components: {
    DashboardLayout,
    UserForm
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: this.$t('Edit Profile'),
      breadcrumbs: [
        { text: this.$t('Edit Profile') }
      ],
      invalid: [],
      isDisabled: false,
      isSaving: false,
      user: this.$store.state.user
    };
  },
  mounted() {
    this.$refs.userForm.setData({
      name: this.user.name,
      email: this.user.email,
      avatar: this.user.avatar,
      bio: this.user.bio
    });
    this.$refs.userForm.resetCleanState();
  },
  beforeRouteLeave(to, from, next) {
    // Check for unsaved changes
    if (this.$refs.userForm.isDirty()) {
      this.$confirm(this.$t('Your changes haven\'t been saved. Are you sure you want to continue?'), {
        title: this.$t('Unsaved Changes'),
        confirmButtonText: this.$t('Continue'),
        cancelButtonText: this.$t('Cancel')
      }).then(() => next());
    } else {
      next();
    }
  },
  methods: {
    // Cancels all changes and goes back to the users page
    handleCancel() {
      this.$router.push({ name: 'Users' });
    },

    // Saves the user
    handleSave(isVerified) {
      const data = this.$refs.userForm.getData();
      const passwordChange = data.password.length > 0;
      this.isSaving = true;

      // If the password is being changed, we need to verify the current password before allowing
      // the request to proceed
      if (passwordChange && !isVerified) {
        this.$http.post('/auth/password/verify', {
          email: data.email,
          password: data.currentPassword
        })
          .then(() => this.handleSave(true))
          .catch(() => {
            this.isSaving = false;
            this.invalid = ['currentPassword'];
            this.$notify({
              type: 'error',
              title: this.$t('Error'),
              message: getErrorMessage('INCORRECT_PASSWORD')
            });
          });

        return;
      }

      // Update the user
      this.$http.put(`/users/${this.$store.state.user.id}`, {
        name: data.name,
        email: data.email,
        avatar: data.avatar,
        bio: data.bio,
        password: passwordChange ? data.password : undefined
      })
        .then(res => {
          this.invalid = [];
          this.$store.commit('setUser', res.data.user);
          this.$refs.userForm.setData({ currentPassword: '', password: '' });
          this.$refs.userForm.resetCleanState();
          this.$notify({
            type: 'success',
            title: this.title,
            message: this.$t('Your changes have been saved.')
          });
        })
        .catch(err => {
          const data = err.response.data;
          this.invalid = Array.isArray(data.invalid) ? data.invalid : [];
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => this.isSaving = false);
    }
  }
};
</script>

<template>
  <dashboard-layout size="small">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- User form -->
    <user-form
      ref="userForm"
      :disabled="isDisabled"
      :invalid="invalid"
      :saving="isSaving"
      is-profile
      @cancel="handleCancel"
      @submit="handleSave"
    />
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
</style>
